import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import _ from 'lodash';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
	iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png'
});

const DrawingControl = ({
	onCircleFilterUpdate,
	onRectFilterUpdate,
	drawingControlEnabled,
	setDrawingControlEnabled,
	circleFilter,
	rectFilter,
	setRestoredFilters,
	restoredFilters
}) => {
	const map = useMap();
	const drawnItemsRef = useRef(new L.FeatureGroup());
	const drawControlRef = useRef(null);
	const currentShapesRef = useRef({
		circle: null,
		rect: null
	});

	const debouncedUpdateCircleFilter = React.useMemo(
		() =>
			_.debounce((circle) => {
				const newObj = {
					lat: circle.getLatLng().lat,
					lng: circle.getLatLng().lng,
					radius: circle.getRadius()
				};
				if (onCircleFilterUpdate) {
					onCircleFilterUpdate(newObj);
				}
				let newRestoredFilters = {
					...restoredFilters,
					circleFilter: newObj
				};
				setRestoredFilters(newRestoredFilters);
			}, 500),
		[onCircleFilterUpdate, restoredFilters, setRestoredFilters]
	);

	const debouncedUpdateRectFilter = React.useMemo(
		() =>
			_.debounce((rectangle) => {
				const bounds = rectangle.getBounds();
				const newRectFilter = {
					north: bounds.getNorth(),
					east: bounds.getEast(),
					south: bounds.getSouth(),
					west: bounds.getWest()
				};
				if (onRectFilterUpdate) {
					onRectFilterUpdate(newRectFilter);
				}
				let newRestoredFilters = {
					...restoredFilters,
					rectFilter: newRectFilter
				};
				setRestoredFilters(newRestoredFilters);
			}, 500),
		[onRectFilterUpdate, restoredFilters, setRestoredFilters]
	);

	useEffect(() => {
		if (!map) return;

		map.addLayer(drawnItemsRef.current);

		const drawControl = new L.Control.Draw({
			position: 'topright',
			draw: drawingControlEnabled
				? {
						circle: {
							shapeOptions: {
								color: '#ff0000',
								weight: 2,
								fillColor: '#ff0000',
								fillOpacity: 0.35
							}
						},
						rectangle: {
							shapeOptions: {
								color: '#ff0000',
								weight: 2,
								fillColor: '#ff0000',
								fillOpacity: 0.35
							}
						},
						polygon: false,
						polyline: false,
						marker: false,
						circlemarker: false
				  }
				: false,
			edit: {
				featureGroup: drawnItemsRef.current,
				remove: false
			}
		});

		if (drawingControlEnabled && !drawControlRef.current) {
			map.addControl(drawControl);
			drawControlRef.current = drawControl;

			map.on('draw:created', function (e) {
				const layer = e.layer;
				const type = e.layerType;

				// Clean up any existing shape of the same type
				if (currentShapesRef.current[type]) {
					drawnItemsRef.current.removeLayer(currentShapesRef.current[type]);
					currentShapesRef.current[type] = null;
				}

				drawnItemsRef.current.addLayer(layer);
				currentShapesRef.current[type] = layer;

				if (type === 'circle') {
					layer.editing.enable();
					debouncedUpdateCircleFilter(layer);
					layer.on('edit', () => {
						debouncedUpdateCircleFilter(layer);
					});
				} else if (type === 'rectangle') {
					layer.editing.enable();
					debouncedUpdateRectFilter(layer);
					layer.on('edit', () => {
						debouncedUpdateRectFilter(layer);
					});
				}

				setDrawingControlEnabled(false);
			});
		} else if (!drawingControlEnabled && drawControlRef.current) {
			map.removeControl(drawControlRef.current);
			drawControlRef.current = null;
		}

		return () => {
			if (drawControlRef.current) {
				map.removeControl(drawControlRef.current);
			}
		};
	}, [
		map,
		drawingControlEnabled,
		debouncedUpdateCircleFilter,
		debouncedUpdateRectFilter,
		setDrawingControlEnabled
	]);

	useEffect(() => {
		// Clean up existing circle first
		if (currentShapesRef.current.circle) {
			drawnItemsRef.current.removeLayer(currentShapesRef.current.circle);
			currentShapesRef.current.circle = null;
		}

		// Add new circle if filter exists
		if (
			circleFilter &&
			circleFilter.lat !== undefined &&
			circleFilter.lng !== undefined &&
			circleFilter.radius !== undefined
		) {
			const circle = L.circle([circleFilter.lat, circleFilter.lng], {
				radius: circleFilter.radius,
				color: '#ff0000',
				weight: 2,
				fillColor: '#ff0000',
				fillOpacity: 0.35
			});
			drawnItemsRef.current.addLayer(circle);
			currentShapesRef.current.circle = circle;
			circle.editing.enable();
			circle.on('edit', () => {
				debouncedUpdateCircleFilter(circle);
			});
		}
	}, [circleFilter, debouncedUpdateCircleFilter]);

	useEffect(() => {
		// Clean up existing rectangle first
		if (currentShapesRef.current.rect) {
			drawnItemsRef.current.removeLayer(currentShapesRef.current.rect);
			currentShapesRef.current.rect = null;
		}

		// Add new rectangle if filter exists
		if (
			rectFilter &&
			rectFilter.north !== undefined &&
			rectFilter.south !== undefined &&
			rectFilter.east !== undefined &&
			rectFilter.west !== undefined
		) {
			const rectangle = L.rectangle(
				[
					[rectFilter.south, rectFilter.west],
					[rectFilter.north, rectFilter.east]
				],
				{
					color: '#ff0000',
					weight: 2,
					fillColor: '#ff0000',
					fillOpacity: 0.35
				}
			);
			drawnItemsRef.current.addLayer(rectangle);
			currentShapesRef.current.rect = rectangle;
			rectangle.editing.enable();
			rectangle.on('edit', () => {
				debouncedUpdateRectFilter(rectangle);
			});
		}
	}, [rectFilter, debouncedUpdateRectFilter]);

	return null;
};

const MapEventHandler = ({ setCenter, setZoom }) => {
	const map = useMap();

	useEffect(() => {
		if (!map) return;

		const handleMoveEnd = () => {
			const newCenter = map.getCenter();
			const centerArray = [newCenter.lat, newCenter.lng];
			setCenter(centerArray);
			sessionStorage.setItem('mapCenter', JSON.stringify(centerArray));
		};

		const handleZoomEnd = () => {
			const newZoom = map.getZoom();
			setZoom(newZoom);
			sessionStorage.setItem('mapZoom', newZoom.toString());
		};

		map.on('moveend', handleMoveEnd);
		map.on('zoomend', handleZoomEnd);

		return () => {
			map.off('moveend', handleMoveEnd);
			map.off('zoomend', handleZoomEnd);
		};
	}, [map, setCenter, setZoom]);

	return null;
};

const LeafletMapFilter = ({
	onRectFilterUpdate,
	onCircleFilterUpdate,
	setRestoredFilters,
	restoredFilters,
	circleFilter,
	rectFilter,
	listings,
	drawingControlEnabled,
	setDrawingControlEnabled
}) => {
	const [center, setCenter] = useState(() => {
		try {
			const stored = sessionStorage.getItem('mapCenter');
			if (stored) {
				const parsed = JSON.parse(stored);
				if (parsed && Array.isArray(parsed) && parsed.length === 2) {
					return parsed;
				}
			}
		} catch (e) {
			console.warn('Invalid mapCenter in session storage');
		}
		return [49.25, -123.1];
	});
	const [zoom, setZoom] = useState(() => {
		try {
			const stored = sessionStorage.getItem('mapZoom');
			if (stored) {
				const parsed = parseInt(stored, 10);
				if (!isNaN(parsed) && parsed >= 1 && parsed <= 20) {
					return parsed;
				}
			}
		} catch (e) {
			console.warn('Invalid mapZoom in session storage');
		}
		return 10;
	});

	return (
		<div style={{ margin: '0', width: '100%', height: '600px' }}>
			<MapContainer center={center} zoom={zoom} style={{ width: '100%', height: '100%' }}>
				<TileLayer
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
				/>
				<MapEventHandler setCenter={setCenter} setZoom={setZoom} />
				<DrawingControl
					onCircleFilterUpdate={onCircleFilterUpdate}
					onRectFilterUpdate={onRectFilterUpdate}
					drawingControlEnabled={drawingControlEnabled}
					setDrawingControlEnabled={setDrawingControlEnabled}
					circleFilter={circleFilter}
					rectFilter={rectFilter}
					setRestoredFilters={setRestoredFilters}
					restoredFilters={restoredFilters}
				/>
			</MapContainer>
		</div>
	);
};

export default LeafletMapFilter;
